import { IZoneCategoryDefaultServiceItem } from 'models';
import { apiVersion } from '../apis.const';
import { transformResponse } from '../apis.util';
import { baseApi } from '../base.api';

/**
 * Create APIs
 */
export const zoneCategoriesApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchZoneCategories: build.query<FetchZoneCategoriesRes, FetchZoneCategoriesReq>({
      query: ({ orgSlug }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/categories`,
      }),
      transformResponse,
      providesTags: ['ZoneCategories'],
    }),

    fetchBuildingZoneCategories: build.query<FetchBuildingZoneCategoriesRes, FetchBuildingZoneCategoriesReq>({
      query: ({ orgSlug, buildingId }) => ({
        url: `/${orgSlug}/${apiVersion}/building/${buildingId}/zone/categories`,
      }),
      transformResponse,
    }),

    createZoneCategory: build.mutation<CreateZoneCategoryRes, CreateZoneCategoryReq>({
      query: ({ orgSlug, ...rest }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/category`,
        method: 'POST',
        body: rest,
      }),
      transformResponse,
      invalidatesTags: ['ZoneCategories'],
    }),

    updateZoneCategory: build.mutation<UpdateZoneCategoryRes, UpdateZoneCategoryReq>({
      query: ({ orgSlug, id, ...rest }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/category/${id}`,
        method: 'PUT',
        body: rest,
      }),
      transformResponse,
      invalidatesTags: ['ZoneCategories'],
    }),

    deleteZoneCategory: build.mutation<DeleteZoneCategoryRes, DeleteZoneCategoryReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/category/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ZoneCategories'],
    }),

    /**
     * Zone category default service items
     */
    fetchZoneCategoryServiceItems: build.query<FetchZoneCategoryServiceItemsRes, FetchZoneCategoryServiceItemsReq>({
      query: ({ orgSlug, categoryId }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/category/${categoryId}/service-items`,
      }),
      transformResponse,
      providesTags: (result, error, { categoryId }) => [{ type: 'ZoneCategoryServiceItems', categoryId }],
    }),

    createZoneCategoryServiceItem: build.mutation<CreateZoneCategoryServiceItemRes, CreateZoneCategoryServiceItemReq>({
      query: ({ orgSlug, categoryId, name, sort_order }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/category/${categoryId}/service-item`,
        method: 'POST',
        body: { name, sort_order },
      }),
      transformResponse,
      invalidatesTags: (result, error, { categoryId }) => [
        { type: 'ZoneCategoryServiceItems', categoryId },
        'ZoneCategories',
      ],
    }),

    updateZoneCategoryServiceItem: build.mutation<UpdateZoneCategoryServiceItemRes, UpdateZoneCategoryServiceItemReq>({
      query: ({ orgSlug, categoryId, id, name, sort_order }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/category/${categoryId}/service-item/${id}`,
        method: 'PUT',
        body: { name, sort_order },
      }),
      transformResponse,
      invalidatesTags: (result, error, { categoryId }) => [
        { type: 'ZoneCategoryServiceItems', categoryId },
        'ZoneCategories',
      ],
    }),

    deleteZoneCategoryServiceItem: build.mutation<DeleteZoneCategoryServiceItemRes, DeleteZoneCategoryServiceItemReq>({
      query: ({ orgSlug, categoryId, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/category/${categoryId}/service-item/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { categoryId }) => [
        { type: 'ZoneCategoryServiceItems', categoryId },
        'ZoneCategories',
      ],
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchZoneCategoriesQuery,
  useFetchBuildingZoneCategoriesQuery,
  useCreateZoneCategoryMutation,
  useUpdateZoneCategoryMutation,
  useDeleteZoneCategoryMutation,
  useFetchZoneCategoryServiceItemsQuery,
  useCreateZoneCategoryServiceItemMutation,
  useUpdateZoneCategoryServiceItemMutation,
  useDeleteZoneCategoryServiceItemMutation,
} = zoneCategoriesApi;

/**
 * Types
 */
type OrgSlug = {
  orgSlug: string;
};
export type ZoneCategory = {
  category: string;
  target_interval: string;
  target_frequency: number;
  building_id?: number | null;
  default_service_items?: IZoneCategoryDefaultServiceItem[];
};

type FetchZoneCategoriesRes = Array<ZoneCategory & { id: number }>;
type FetchZoneCategoriesReq = OrgSlug;
type FetchBuildingZoneCategoriesRes = any;
type FetchBuildingZoneCategoriesReq = OrgSlug & {
  buildingId: number;
};
type CreateZoneCategoryRes = any;
type CreateZoneCategoryReq = OrgSlug & ZoneCategory;
type UpdateZoneCategoryRes = any;
type UpdateZoneCategoryReq = OrgSlug &
  ZoneCategory & {
    id: number;
  };
type DeleteZoneCategoryRes = any;
type DeleteZoneCategoryReq = OrgSlug & {
  id: number;
};

type CategoryServiceItemBase = OrgSlug & { categoryId: number };
type FetchZoneCategoryServiceItemsRes = IZoneCategoryDefaultServiceItem[];
type FetchZoneCategoryServiceItemsReq = CategoryServiceItemBase;
type CreateZoneCategoryServiceItemRes = IZoneCategoryDefaultServiceItem;
type CreateZoneCategoryServiceItemReq = CategoryServiceItemBase & { name: string; sort_order?: number };
type UpdateZoneCategoryServiceItemRes = IZoneCategoryDefaultServiceItem;
type UpdateZoneCategoryServiceItemReq = CategoryServiceItemBase & { id: number; name: string; sort_order?: number };
type DeleteZoneCategoryServiceItemRes = any;
type DeleteZoneCategoryServiceItemReq = CategoryServiceItemBase & { id: number };
